import { TGetOrganizationListDto } from "@/api/services/admin/organization/dtos/get-organization-list.dto";
import { IconStar, IconStarOutline } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { button, text } from "@/theme/colors";
import { EProductCode } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Radio, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

const BADGE_PRODUCT_CODES = [EProductCode.VISA, EProductCode.ACCOUNT, EProductCode.STUDY];

type TUseOrganizationSelectColumnsParams = {
  selectedOrgId: string;
  onSelectOrg: (id: string) => void;
};

export const useOrganizationSelectColumns = ({ selectedOrgId, onSelectOrg }: TUseOrganizationSelectColumnsParams) => {
  const columns = useMemo<MRT_ColumnDef<TGetOrganizationListDto>[]>(
    () => [
      {
        id: "select",
        header: "",
        size: 48,
        enableSorting: false,
        Cell: ({ row }) => {
          const orgId = String(row.original.id);
          return (
            <Radio
              size="small"
              checked={orgId === selectedOrgId}
              onChange={() => onSelectOrg(orgId)}
              sx={{ color: button.primary, "&.Mui-checked": { color: button.primary } }}
            />
          );
        },
      },
      {
        accessorKey: "externalId",
        header: "組織ID",
        size: 130,
        Header: () => (
          <TableSorting name="externalId">
            <Typography variant="body14Bold" color={text.primary}>
              組織ID
            </Typography>
          </TableSorting>
        ),
      },
      {
        accessorKey: "name",
        header: "組織名",
        size: 240,
        Header: () => (
          <TableSorting name="name">
            <Typography variant="body14Bold" color={text.primary}>
              組織名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
            {row.original?.isPinned ? <IconStar /> : <IconStarOutline />}
            <EllipsisText title={row.original?.name ?? EMPTY_STR.TEXT} variant="body14" maxWidth={190} />
          </Stack>
        ),
      },
      {
        id: "products",
        header: "契約サービス",
        size: 160,
        Cell: ({ row }) => {
          const products = BADGE_PRODUCT_CODES.map((code) => row.original?.productContracts?.find((c) => c.product.code === code)?.product)
            .filter((product): product is NonNullable<typeof product> => Boolean(product))
            .map((product) => ({ code: product.code, singleCharLabel: product.singleCharLabel, name: product.name }));
          return products.length > 0 ? <ContractServiceBadge products={products} /> : EMPTY_STR.TEXT;
        },
      },
      {
        accessorKey: "createdAt",
        header: "登録日",
        size: 106,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original?.createdAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
    ],
    [selectedOrgId, onSelectOrg],
  );

  return { columns };
};
